import React, { forwardRef, useMemo } from 'react';

import { FormattedMessage } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, Pressable, Text, xcss } from '@atlaskit/primitives';

import { type SocialSignalType } from '../../../../common/types/social-signals';

import { messages } from './messages';

interface ShortInlineSocialSignalProps {
	avatarUrl: string;
	componentName?: string;
	onClick?: VoidFunction;
	socialSignalType: SocialSignalType;
	testId?: string;
	/**
	 * The name for the user.
	 * **NOTE:** only the first name will be displayed.
	 **/
	userName: string;
}

const signalTextXcss = xcss({
	color: 'color.text.subtle',
	':hover': {
		textDecoration: 'underline',
	},
	':focus-visible': {
		textDecoration: 'underline',
	},
	background: 'none',
	padding: '0',
});

export const ShortInlineSocialSignal: React.ForwardRefExoticComponent<
	ShortInlineSocialSignalProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, ShortInlineSocialSignalProps>(
	({ avatarUrl, userName, onClick, socialSignalType, testId, componentName }, ref) => {
		const firstName = useMemo(() => userName.split(' ')[0], [userName]);

		const message = messages[socialSignalType];
		if (!message) {
			return null;
		}

		return (
			<Pressable
				ref={ref}
				onClick={onClick}
				xcss={signalTextXcss}
				testId={testId}
				componentName={componentName}
			>
				<Flex gap="space.075" alignItems="center">
					<Avatar src={avatarUrl} size="xsmall" />
					<Text size="small" color="color.text.subtle">
						<FormattedMessage {...message} values={{ name: firstName }} />
					</Text>
				</Flex>
			</Pressable>
		);
	},
);
